import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Input, Label, Button, Switch,
} from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import { readApiError } from '../../lib/apiError';
import { Loader2, Plus, Tags, Trash2 } from 'lucide-react';
import api from '../../lib/api';

interface VehicleCategoryRow {
  id: number;
  key: string;
  label: string;
  day_price_cents: number;
  week_price_cents: number | null;
  sort_order: number;
  is_active: boolean;
  vehicle_count: number;
}

function centsToEuro(cents: number | null | undefined): string {
  return ((cents ?? 0) / 100).toFixed(2);
}

function euroToCents(value: string): number {
  return Math.round(parseFloat(value.replace(',', '.')) * 100) || 0;
}

export default function VehicleCategories() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const { data: categories, isLoading } = useQuery({
    queryKey: ['vehicle-categories-settings'],
    queryFn: async () => {
      const response = await api.get<VehicleCategoryRow[]>('/categories');
      return response.data;
    },
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['vehicle-categories-settings'] });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<VehicleCategoryRow> & { id: number }) => {
      await api.put(`/categories/${id}`, data);
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Gespeichert' });
    },
    onError: (error) => {
      toast({
        variant: 'destructive', title: 'Fehler',
        description: readApiError(error, 'Speichern fehlgeschlagen'),
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      await api.post('/categories', {
        key: newKey.trim(),
        label: newLabel.trim() || newKey.trim(),
        day_price_cents: 0,
        sort_order: (categories?.length ?? 0) + 1,
      });
    },
    onSuccess: () => {
      invalidate();
      setNewKey('');
      setNewLabel('');
      toast({ title: 'Kategorie angelegt' });
    },
    onError: (error) => {
      toast({
        variant: 'destructive', title: 'Fehler',
        description: readApiError(error, 'Kategorie konnte nicht angelegt werden'),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/categories/${id}`);
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Kategorie gelöscht' });
    },
    onError: (error) => {
      toast({
        variant: 'destructive', title: 'Fehler',
        description: readApiError(error, 'Kategorie konnte nicht gelöscht werden'),
      });
    },
  });

  if (isLoading) {
    return (
      <Card variant="glass">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card variant="glass">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <Tags className="h-5 w-5" />
            Fahrzeugkategorien
          </CardTitle>
          <CardDescription>
            Tages- und Wochenpreise je Kategorie — diese Preise werden Kunden auf der
            Online-Buchungsseite angezeigt, unabhängig vom Preis des später zugewiesenen
            Fahrzeugs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(categories ?? []).map((category) => (
            <div
              key={category.id}
              className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto_auto] gap-3 items-end
                        rounded-md border border-border p-3"
            >
              <div>
                <Label className="text-xs text-muted-foreground">
                  {category.label} ({category.key})
                  {category.vehicle_count > 0 && (
                    <span className="ml-1">· {category.vehicle_count} Fahrzeug(e)</span>
                  )}
                </Label>
                <Input
                  className="mt-1"
                  defaultValue={category.label}
                  onBlur={(e) => {
                    if (e.target.value !== category.label) {
                      updateMutation.mutate({ id: category.id, label: e.target.value });
                    }
                  }}
                />
              </div>
              <div className="w-28">
                <Label className="text-xs text-muted-foreground">Tagespreis</Label>
                <Input
                  className="mt-1"
                  defaultValue={centsToEuro(category.day_price_cents)}
                  onBlur={(e) => {
                    const cents = euroToCents(e.target.value);
                    if (cents !== category.day_price_cents) {
                      updateMutation.mutate({ id: category.id, day_price_cents: cents });
                    }
                  }}
                />
              </div>
              <div className="w-28">
                <Label className="text-xs text-muted-foreground">Wochenpreis</Label>
                <Input
                  className="mt-1"
                  placeholder="optional"
                  defaultValue={category.week_price_cents != null
                    ? centsToEuro(category.week_price_cents) : ''}
                  onBlur={(e) => {
                    const raw = e.target.value.trim();
                    const cents = raw ? euroToCents(raw) : null;
                    if (cents !== category.week_price_cents) {
                      updateMutation.mutate({ id: category.id, week_price_cents: cents });
                    }
                  }}
                />
              </div>
              <div className="flex flex-col items-center gap-1">
                <Label className="text-xs text-muted-foreground">Aktiv</Label>
                <Switch
                  checked={category.is_active}
                  onCheckedChange={(value) =>
                    updateMutation.mutate({ id: category.id, is_active: value })
                  }
                />
              </div>
              <Button
                type="button" variant="ghost" size="sm"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  if (category.vehicle_count > 0) {
                    toast({
                      variant: 'destructive', title: 'Nicht möglich',
                      description: 'Dieser Kategorie sind noch Fahrzeuge zugeordnet.',
                    });
                    return;
                  }
                  if (window.confirm(`Kategorie „${category.label}“ wirklich löschen?`)) {
                    deleteMutation.mutate(category.id);
                  }
                }}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end
                          rounded-md border border-dashed border-border p-3">
            <div>
              <Label className="text-xs text-muted-foreground">
                Neuer Schlüssel (z.B. "cabrio")
              </Label>
              <Input
                className="mt-1" value={newKey}
                onChange={(e) => setNewKey(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                placeholder="cabrio"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Bezeichnung</Label>
              <Input
                className="mt-1" value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Cabrio"
              />
            </div>
            <Button
              type="button" disabled={!newKey.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              <Plus className="mr-2 h-4 w-4" />
              Hinzufügen
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
